import { useCallback, useEffect, useRef } from 'react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/core/firebase/client'
import { withRequestId } from '@/core/firebase/idempotency'
import { logger } from '@/core/logger'
import { getOrCreatePreviewVisitorId } from '../domain/visitor-id'

type Step = { path: string; at: number }

// Client-side caps so a single demo session writes a bounded number of events
// (the CF enforces a hard cap too). FLUSH_MS debounces a burst of navigations
// into one CF call; FLUSH_AT flushes early once the buffer fills.
const SESSION_STEP_CAP = 50
const FLUSH_MS = 4000
const FLUSH_AT = 5

/**
 * Records the screens a preview visitor views inside the demo — the "Recorrido"
 * shown in the admin tokens drawer. No-op unless `active` (a redeemed preview
 * session). Buffers each route change and flushes batches through the
 * `record_preview_screen_views` CF (the only writer — `preview_events` is
 * read-only at the rules layer). Best-effort: a failed flush is dropped, never
 * surfaced to the visitor.
 */
export function usePreviewScreenTrail(
  clubId: string | null,
  active: boolean,
  pathname: string | undefined,
): void {
  const buffer = useRef<Step[]>([])
  const lastPath = useRef<string | null>(null)
  const total = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const visitorId = useRef<string>('')
  if (active && !visitorId.current) visitorId.current = getOrCreatePreviewVisitorId()

  const flush = useCallback(() => {
    if (!clubId || buffer.current.length === 0) return
    const steps = buffer.current
    buffer.current = []
    const fn = httpsCallable<
      { clubId: string; visitorId: string; steps: Step[]; requestId: string },
      { recorded: number }
    >(functions, 'record_preview_screen_views')
    void fn(withRequestId({ clubId, visitorId: visitorId.current, steps })).catch((err) => {
      logger.w('record_preview_screen_views failed (telemetry, ignored)', err)
    })
  }, [clubId])

  useEffect(() => {
    if (!active || !clubId || !pathname) return
    if (lastPath.current === pathname) return // dedupe consecutive same-screen
    lastPath.current = pathname
    if (total.current >= SESSION_STEP_CAP) return
    total.current += 1
    buffer.current.push({ path: pathname, at: Date.now() })

    if (timer.current) clearTimeout(timer.current)
    if (buffer.current.length >= FLUSH_AT) {
      flush()
    } else {
      timer.current = setTimeout(flush, FLUSH_MS)
    }
  }, [active, clubId, pathname, flush])

  // Flush whatever's buffered when the tab is hidden/closed or the session ends.
  useEffect(() => {
    if (!active) return
    const onHide = () => flush()
    window.addEventListener('pagehide', onHide)
    return () => {
      window.removeEventListener('pagehide', onHide)
      if (timer.current) clearTimeout(timer.current)
      flush()
    }
  }, [active, flush])
}
